import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Plus, Send, Eye, Edit, XCircle, CheckCircle,
  Clock, AlertTriangle, IndianRupee,
} from "lucide-react";
import { Form } from "react-bootstrap";
import {
  PageHeader, StatsCard, AppButton, AppBadge, SearchInput,
  EmptyState,
} from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import "./service-invoices.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const STATUS_CONFIG = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  sent: { label: "Sent", variant: "info", icon: Send },
  viewed: { label: "Viewed", variant: "primary", icon: Eye },
  pending_payment: { label: "Pending Payment", variant: "warning", icon: Clock },
  paid: { label: "Paid", variant: "success", icon: CheckCircle },
  overdue: { label: "Overdue", variant: "danger", icon: AlertTriangle },
  cancelled: { label: "Cancelled", variant: "secondary", icon: XCircle },
};

const SERVICE_TYPE_LABELS = {
  custom_development: "Custom Development",
  integration: "Integration",
  support: "Support",
  amc: "AMC",
  training: "Training",
  others: "Others",
};

const ServiceInvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;

      const { data } = await apiClient.get("/sa/service-invoices", { params });
      setInvoices(data.invoices || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({ show: true, message: "Failed to load invoices.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/sa/service-invoices/stats");
      setStats(data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSend = async (id) => {
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/send`);
      setToast({ show: true, message: "Invoice sent successfully.", type: "success" });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to send.", type: "error" });
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this invoice?")) return;
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/cancel`);
      setToast({ show: true, message: "Invoice cancelled.", type: "success" });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to cancel.", type: "error" });
    }
  };

  const handlePreviewPdf = async (id) => {
    try {
      const response = await apiClient.get(`/sa/service-invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to generate PDF.", type: "error" });
    }
  };

  const handleApprovePayment = async (id) => {
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/approve-payment`);
      setToast({ show: true, message: "Payment approved.", type: "success" });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to approve.", type: "error" });
    }
  };

  const handleRejectPayment = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):");
    if (reason === null) return; // cancelled
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/reject-payment`, { reason });
      setToast({ show: true, message: "Payment rejected.", type: "success" });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to reject.", type: "error" });
    }
  };

  return (
    <div className="si-page">
      <PageHeader
        icon={FileText}
        title="Service Invoices"
        description="Create and manage service invoices for institutes"
        actions={
          <AppButton onClick={() => navigate("/sa/service-invoices/create")}>
            <Plus size={16} /> New Invoice
          </AppButton>
        }
      />

      {/* Stats Cards */}
      <div className="si-stats-grid">
        <StatsCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} icon={IndianRupee} iconColor="emerald" />
        <StatsCard title="Outstanding" value={formatCurrency(stats.totalOutstanding)} icon={Clock} iconColor="amber" />
        <StatsCard title="Paid" value={stats.paid || 0} icon={CheckCircle} iconColor="emerald" />
        <StatsCard title="Overdue" value={stats.overdue || 0} icon={AlertTriangle} iconColor="rose" />
        <StatsCard title="Pending Approval" value={stats.pendingApproval || 0} icon={Clock} iconColor="blue" />
      </div>

      {/* Filters */}
      <div className="si-filters">
        <SearchInput
          value={search}
          onChange={(val) => { setSearch(val); setPage(1); }}
          placeholder="Search invoices..."
        />
        <Form.Select
          className="si-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="viewed">Viewed</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="si-loading">Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<FileText size={40} />}
          title="No invoices found"
          description={search || statusFilter ? "Try adjusting your filters." : "Create your first service invoice to get started."}
        />
      ) : (
        <div className="si-table-wrapper">
          <table className="si-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Institute</th>
                <th>Service Type</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const sc = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                return (
                  <tr key={inv._id}>
                    <td className="si-invoice-num">{inv.invoiceNumber}</td>
                    <td>{inv.instituteId?.name || "—"}</td>
                    <td>{SERVICE_TYPE_LABELS[inv.serviceType] || inv.serviceType}</td>
                    <td className="si-amount">{formatCurrency(inv.totalAmount)}</td>
                    <td className={inv.status === "overdue" ? "si-overdue-date" : ""}>{formatDate(inv.dueDate)}</td>
                    <td>
                      <AppBadge variant={sc.variant}>{sc.label}</AppBadge>
                      {inv.version > 1 && <span className="si-version">v{inv.version}</span>}
                    </td>
                    <td className="si-actions">
                      <button className="si-action-btn" onClick={() => navigate(`/sa/service-invoices/${inv._id}`)} title="View">
                        <Eye size={15} />
                      </button>
                      <button className="si-action-btn" onClick={() => handlePreviewPdf(inv._id)} title="Preview PDF">
                        <FileText size={15} />
                      </button>
                      {(inv.status === "draft" || inv.status === "sent" || inv.status === "viewed" || inv.status === "overdue") && (
                        <button className="si-action-btn" onClick={() => navigate(`/sa/service-invoices/edit/${inv._id}`)} title="Edit">
                          <Edit size={15} />
                        </button>
                      )}
                      {inv.status === "draft" && (
                        <button className="si-action-btn si-action-send" onClick={() => handleSend(inv._id)} title="Send">
                          <Send size={15} />
                        </button>
                      )}
                      {inv.status !== "paid" && inv.status !== "cancelled" && (
                        <button className="si-action-btn si-action-cancel" onClick={() => handleCancel(inv._id)} title="Cancel">
                          <XCircle size={15} />
                        </button>
                      )}
                      {inv.payment?.approvalStatus === "pending" && (
                        <>
                          <button className="si-action-btn si-action-approve" onClick={() => handleApprovePayment(inv._id)} title="Approve Payment">
                            <CheckCircle size={15} />
                          </button>
                          <button className="si-action-btn si-action-reject" onClick={() => handleRejectPayment(inv._id)} title="Reject Payment">
                            <XCircle size={15} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="si-pagination">
          <AppButton
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </AppButton>
          <span className="si-page-info">Page {page} of {totalPages}</span>
          <AppButton
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </AppButton>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default ServiceInvoicesPage;
